import { goRequest } from '../utils/httpClientTs';
import type { GroupTypeRoleSelection } from '../types/group';

export interface AudienceCriterion {
  scope: string;
  group_type_id: string;
  all_roles: boolean;
  role_ids?: string[];
}

export interface AudienceCriteria {
  criteria: AudienceCriterion[];
  active_members_only: boolean;
  legacy_audience_ids?: string[];
}

export interface RecipientPreview {
  user_id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface EmailCampaign {
  id: string;
  name: string;
  status: string;
  template_id?: string | null;
  subject?: string | null;
  audience_ids?: string;
  audience_names?: string | null;
  audience_criteria?: AudienceCriteria | null;
  estimated_recipient_count?: number | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  created_at: string;
  created_by_id?: string | null;
  recipient_count?: number | null;
  sent_count?: number | null;
  delivered_count?: number | null;
  opened_count?: number | null;
  clicked_count?: number | null;
  bounce_count?: number | null;
  last_stats_at?: string | null;
}

export interface EmailCampaignListItem {
  id: string;
  name: string;
  status: string;
  template_id?: string | null;
  subject?: string | null;
  audience_ids?: string;
  audience_names?: string | null;
  audience_criteria?: AudienceCriteria | null;
  estimated_recipient_count?: number | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  created_at: string;
}

export interface CreateEmailCampaignRequest {
  name: string;
  template_id?: string;
  subject?: string;
  audience_ids?: string[];
  audience_criteria?: AudienceCriteria;
}

export interface UpdateEmailCampaignRequest {
  name?: string;
  template_id?: string;
  subject?: string;
  audience_ids?: string[];
  audience_criteria?: AudienceCriteria;
}

export interface ScheduleEmailCampaignRequest {
  scheduled_at: string; // ISO datetime
}

export interface ListEmailCampaignsParams {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
}

export interface ListEmailCampaignsResult {
  items: EmailCampaignListItem[];
  total_count: number;
  page: number;
  per_page: number;
}

export interface Audience {
  id: string;
  name: string;
}

export interface AudienceEstimateResponse {
  count: number;
}

export interface AudiencePreviewResponse {
  items: RecipientPreview[];
  total_count: number;
  page: number;
  per_page: number;
}

export interface CampaignRecipientsResult {
  items: RecipientPreview[];
  total_count: number;
  page: number;
  per_page: number;
}

export const buildAudienceCriteria = (
  criteria: GroupTypeRoleSelection[],
  activeMembersOnly: boolean,
  legacyAudienceIds: string[]
): AudienceCriteria => ({
  criteria: criteria.map((c) => ({
    scope: c.scope,
    group_type_id: c.group_type_id,
    all_roles: c.all_roles,
    role_ids: c.all_roles ? undefined : c.role_ids,
  })),
  active_members_only: activeMembersOnly,
  legacy_audience_ids:
    legacyAudienceIds.length > 0 ? legacyAudienceIds : undefined,
});

export const hasAudienceSource = (ac?: AudienceCriteria | null): boolean => {
  if (!ac) return false;
  if (ac.criteria?.length) return true;
  return (ac.legacy_audience_ids ?? []).some((id) => id.trim() !== '');
};

export const getEmailCampaigns = (
  params?: ListEmailCampaignsParams
): Promise<ListEmailCampaignsResult> => {
  const searchParams = new URLSearchParams();
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.per_page != null)
    searchParams.set('per_page', String(params.per_page));
  if (params?.status) searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);
  const qs = searchParams.toString();
  return goRequest<ListEmailCampaignsResult>({
    url: `/api/email-campaigns${qs ? `?${qs}` : ''}`,
    method: 'GET',
  });
};

export const getEmailCampaign = (id: string): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}`,
    method: 'GET',
  });
};

export const createEmailCampaign = (
  data: CreateEmailCampaignRequest
): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: '/api/email-campaigns',
    method: 'POST',
    data,
  });
};

export const updateEmailCampaign = (
  id: string,
  data: UpdateEmailCampaignRequest
): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}`,
    method: 'PATCH',
    data,
  });
};

export const scheduleEmailCampaign = (
  id: string,
  data: ScheduleEmailCampaignRequest
): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}/schedule`,
    method: 'POST',
    data,
  });
};

export const cancelEmailCampaign = (id: string): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}/cancel`,
    method: 'POST',
  });
};

export const deleteEmailCampaign = (id: string): Promise<void> => {
  return goRequest<void>({
    url: `/api/email-campaigns/${id}`,
    method: 'DELETE',
  });
};

export const getEmailCampaignAudiences = (): Promise<Audience[]> => {
  return goRequest<Audience[]>({
    url: '/api/email-campaigns/audiences',
    method: 'GET',
  });
};

export const estimateEmailCampaignAudience = (
  audienceCriteria: AudienceCriteria
): Promise<AudienceEstimateResponse> => {
  return goRequest<AudienceEstimateResponse>({
    url: '/api/email-campaigns/audiences/estimate',
    method: 'POST',
    data: { audience_criteria: audienceCriteria },
  });
};

export const previewEmailCampaignAudience = (
  audienceCriteria: AudienceCriteria,
  page = 1,
  perPage = 25
): Promise<AudiencePreviewResponse> => {
  return goRequest<AudiencePreviewResponse>({
    url: '/api/email-campaigns/audiences/preview',
    method: 'POST',
    data: {
      audience_criteria: audienceCriteria,
      page,
      per_page: perPage,
    },
  });
};

export const getEmailCampaignRecipients = (
  id: string,
  page = 1,
  perPage = 25
): Promise<CampaignRecipientsResult> => {
  const qs = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  return goRequest<CampaignRecipientsResult>({
    url: `/api/email-campaigns/${id}/recipients?${qs}`,
    method: 'GET',
  });
};
